/**
 * Document Types Configuration
 *
 * Defines all accounting document types with their properties.
 * Used by the accounting module components for routes, labels, statuses, etc.
 *
 * `defaultsShippingAddress` marks the two types that are about moving goods —
 * a sales order and a purchase order. Picking a party on those prefills a
 * shipping address; everywhere else a document starts with a billing address
 * alone, and a shipping one only appears if someone adds it or if the document
 * was converted from one that carried it.
 *
 * Every label here is a **getter**, never a resolved string. This module is
 * evaluated when it is imported, which happens before `/api/me` tells the app
 * which language the user reads in — a string resolved at that moment freezes
 * the English fallback for the rest of the session. That is what made a quote
 * read « En attente d'approbation » in the list (that screen translates while
 * rendering) and "Waiting approval" in the Associés and Versions tabs, which
 * read their label from here. Reading through a getter also makes the label
 * follow a locale change, since `i18n.t` tracks the locale ref.
 */

/**
 * One document status: its stored value, its translated label and its colour.
 *
 * @param {string} value stored status value ('waiting_approval')
 * @param {string} key translation key ('Waiting approval')
 * @param {string} cls text colour class
 */
function status(value, key, cls = '') {
    return {
        value,
        class: cls,
        get label() {
            return i18n.t(key);
        }
    };
}

/**
 * One entry of a list's status filter dropdown. Filters carry derived states
 * (`not_paid`, `overdue`) that no document ever stores, hence the separate
 * shape without a colour.
 */
function filter(value, key) {
    return {
        value,
        get label() {
            return i18n.t(key);
        }
    };
}

/**
 * Build a document type, translating its two names lazily.
 *
 * @param {{name: string, nameSingular: string}} config translation keys plus
 *        the type's remaining, language-independent configuration
 */
function docType({ name, nameSingular, ...rest }) {
    return {
        get name() {
            return i18n.t(name);
        },
        get nameSingular() {
            return i18n.t(nameSingular);
        },
        ...rest
    };
}

export const documentTypes = {
    // Sales (Customer)
    'sales/quotes': docType({
        name: 'Quotes',
        nameSingular: 'Quote',
        category: 'sales',
        contactType: 'customer',
        statuses: [
            status('draft', 'Draft'),
            status('waiting_approval', 'Waiting approval', 'has-text-warning'),
            status('approved', 'Approved', 'has-text-link'),
            status('sent', 'Sent', 'has-text-info'),
            status('accepted', 'Accepted', 'has-text-success'),
            status('refused', 'Refused', 'has-text-danger'),
            status('expired', 'Expired', 'has-text-warning'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        prefix: 'QUO',
        canConvertTo: ['sales/orders', 'sales/invoices'],
        metaFields: { date: true, expiration: true }
    }),
    'sales/orders': docType({
        name: 'Sales Orders',
        nameSingular: 'Sales Order',
        category: 'sales',
        contactType: 'customer',
        statuses: [
            status('draft', 'Draft'),
            status('confirmed', 'Confirmed', 'has-text-info'),
            status('shipped', 'Shipped', 'has-text-success'),
            status('invoiced', 'Invoiced', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        prefix: 'SO',
        canConvertTo: ['sales/invoices'],
        metaFields: { date: true },
        defaultsShippingAddress: true
    }),
    'sales/invoices': docType({
        name: 'Invoices',
        nameSingular: 'Invoice',
        category: 'sales',
        contactType: 'customer',
        statuses: [
            // The model a recurring schedule clones from. It never reaches a
            // list, but it is rendered from the schedule's preview.
            status('template', 'Template', 'has-text-info'),
            status('draft', 'Draft'),
            status('sent', 'Sent', 'has-text-info'),
            status('paid', 'Paid', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        filterOptions: [
            filter('draft', 'Draft'),
            filter('not_paid', 'Not paid'),
            filter('partial', 'Partially paid'),
            filter('overdue', 'Overdue'),
            filter('paid', 'Paid'),
            filter('canceled', 'Canceled')
        ],
        prefix: 'INV',
        canConvertTo: ['sales/credit-notes'],
        metaFields: { date: true, terms: true, due_date: true }
    }),
    'sales/credit-notes': docType({
        name: 'Credit Notes',
        nameSingular: 'Credit Note',
        category: 'sales',
        contactType: 'customer',
        statuses: [
            status('draft', 'Draft'),
            status('issued', 'Issued', 'has-text-info'),
            status('partial', 'Partial', 'has-text-warning'),
            status('applied', 'Applied', 'has-text-success'),
            status('refunded', 'Refunded', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        prefix: 'CN',
        canConvertTo: [],
        metaFields: { date: true }
    }),

    // Purchases (Supplier)
    'purchases/orders': docType({
        name: 'Purchase Orders',
        nameSingular: 'Purchase Order',
        category: 'purchases',
        contactType: 'supplier',
        statuses: [
            status('draft', 'Draft'),
            status('sent', 'Sent', 'has-text-info'),
            status('received', 'Received', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        prefix: 'PO',
        canConvertTo: ['purchases/invoices'],
        metaFields: { date: true },
        defaultsShippingAddress: true
    }),
    'purchases/invoices': docType({
        name: 'Bills',
        nameSingular: 'Bill',
        category: 'purchases',
        contactType: 'supplier',
        statuses: [
            // The model a recurring schedule clones from. It never reaches a
            // list, but it is rendered from the schedule's preview.
            status('template', 'Template', 'has-text-info'),
            status('draft', 'Draft'),
            status('received', 'Received', 'has-text-info'),
            status('paid', 'Paid', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        filterOptions: [
            filter('draft', 'Draft'),
            filter('not_paid', 'Not paid'),
            filter('partial', 'Partially paid'),
            filter('overdue', 'Overdue'),
            filter('paid', 'Paid'),
            filter('canceled', 'Canceled')
        ],
        prefix: 'BILL',
        canConvertTo: [],
        metaFields: { date: true, terms: true, due_date: true }
    }),
    'purchases/credit-notes': docType({
        name: 'Purchase Credit Notes',
        nameSingular: 'Purchase Credit Note',
        category: 'purchases',
        contactType: 'supplier',
        statuses: [
            status('draft', 'Draft'),
            status('issued', 'Issued', 'has-text-info'),
            status('partial', 'Partial', 'has-text-warning'),
            status('applied', 'Applied', 'has-text-success'),
            status('refunded', 'Refunded', 'has-text-success'),
            status('canceled', 'Canceled', 'has-text-grey')
        ],
        prefix: 'SCN',
        canConvertTo: [],
        metaFields: { date: true }
    })
};

/**
 * Get document type configuration by key
 * @param {string} key - Document type key (e.g., 'sales/invoices')
 * @returns {Object|null}
 */
/**
 * Document type key → the morph alias the backend knows it by.
 *
 * Used by the polymorphic tabs (comments, documents, ledger trail), which all
 * address a record as `<alias>/<id>`. Shared rather than repeated so the two
 * never disagree about what a supplier credit note is called.
 */
export const DOC_TYPE_ENTITY_MAP = {
    'sales/invoices': 'invoice',
    'sales/quotes': 'quote',
    'sales/credit-notes': 'credit_note',
    'sales/orders': 'sales_order',
    'purchases/invoices': 'bill',
    'purchases/credit-notes': 'supplier_credit_note',
    'purchases/orders': 'purchase_order'
};

/**
 * Document type key → the ListColumnCatalog object type its list is registered
 * under. The others keep the classic narrow list + preview layout and have no
 * column manager, so they have no per-list user preference either.
 *
 * Shared because both ends read it: the list, to offer the manager, and the
 * document's fiche, to find the user's choice for the list it was opened from.
 */
export const DOC_TYPE_LIST_OBJECT_TYPE = {
    'sales/quotes': 'quotes',
    'sales/orders': 'sales_orders',
    'sales/invoices': 'invoices'
};

/**
 * The list object type for a document type, or '' when its list is not managed.
 *
 * @param {?string} key Document type key, e.g. `sales/invoices`
 * @return {string}
 */
export function listObjectTypeForDocType(key) {
    return DOC_TYPE_LIST_OBJECT_TYPE[key] || '';
}

export function getDocType(key) {
    return documentTypes[key] || null;
}

/**
 * Extract document type key from route path
 * @param {Object} route - Vue Router route object
 * @returns {string|null}
 */
export function getDocTypeFromRoute(route) {
    const match = route.path.match(/\/(sales|purchases)\/(quotes|orders|invoices|credit-notes)/);
    return match ? `${match[1]}/${match[2]}` : null;
}

/**
 * Check if a document type key is valid
 * @param {string} key - Document type key
 * @returns {boolean}
 */
export function isValidDocType(key) {
    return key in documentTypes;
}

/**
 * Get status options for a document type
 * @param {string} docTypeKey - Document type key
 * @returns {Array}
 */
export function getStatusOptions(docTypeKey) {
    const docType = getDocType(docTypeKey);
    return docType ? docType.statuses : [];
}

/**
 * Get filter dropdown options for a document type. Falls back to statuses
 * for doc types that don't define a separate filter list (quotes, orders,
 * credit notes, recurring).
 * @param {string} docTypeKey - Document type key
 * @returns {Array}
 */
export function getFilterOptions(docTypeKey) {
    const docType = getDocType(docTypeKey);
    if (!docType) return [];
    return docType.filterOptions ?? docType.statuses;
}

/**
 * Get route names for a document type
 * @param {string} docTypeKey - Document type key (e.g., 'sales/invoices')
 * @returns {Object} Route names object
 */
export function getRouteNames(docTypeKey) {
    const [category, type] = docTypeKey.split('/');
    const singular = type.replace(/s$/, ''); // invoices -> invoice

    return {
        list: `${category}-${type}`, // sales-invoices
        preview: `${category}-${singular}-preview`, // sales-invoice-preview
        overview: `${category}-${singular}-overview`, // sales-invoice-overview
        comments: `${category}-${singular}-comments`, // sales-invoice-comments
        documents: `${category}-${type}-documents`, // sales-invoices-documents
        related: `${category}-${type}-related`, // sales-invoices-related
        versions: `${category}-${singular}-versions`, // sales-quote-versions
        logs: `${category}-${singular}-logs`, // sales-invoice-logs
        new: `${category}-${singular}-new`, // sales-invoice-new
        edit: `${category}-${singular}-edit`, // sales-invoice-edit
        sendEmail: `${category}-${singular}-send-email` // sales-invoice-send-email
    };
}

/**
 * Get all document types for a category
 * @param {string} category - 'sales' or 'purchases'
 * @returns {Object} Filtered document types
 */
export function getDocTypesByCategory(category) {
    return Object.fromEntries(Object.entries(documentTypes).filter(([key]) => key.startsWith(category)));
}
